import { useMemo, useState } from 'react';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { PublicKey } from '@solana/web3.js';
import { useSnackbar } from 'notistack';
import DialogForm from '../DialogForm';
import LoadingIndicator from '../LoadingIndicator';
import strings from '../../localization';
import { useChainClock, useHoldings, usePoolInfo, usePosition, useStake } from '../../utils/tokenStaking/hooks';
import {
  formatBps,
  formatUnits,
  lockReservation,
  projectedRewards,
  toUnits,
} from '../../utils/tokenStaking/program';
import { totalHoldings } from '../../utils/tokenStaking/transactions';

const DAY = 86_400;
const AMOUNT_PATTERN = /^\d*\.?\d*$/;

export interface TokenStakeDialogProps {
  open: boolean;
  onClose: () => void;
  mint: PublicKey;
  symbol: string;
}

export function formatDate(unixSeconds: bigint | number): string {
  return new Date(Number(unixSeconds) * 1000).toLocaleString();
}

export default function TokenStakeDialog({ open, onClose, mint, symbol }: TokenStakeDialogProps) {
  const t = strings.tokenStaking;
  const { enqueueSnackbar } = useSnackbar();
  const { data: info, isLoading } = usePoolInfo(mint);
  const { data: position } = usePosition(mint);
  const holdings = useHoldings(info?.pool);
  const clock = useChainClock();
  const { mutateAsync, isLoading: sending } = useStake(info?.pool);
  const [input, setInput] = useState('');
  const [confirming, setConfirming] = useState(false);

  const pool = info?.pool;
  const decimals = info?.decimals ?? 0;
  const balance = totalHoldings(holdings);
  const amount = useMemo(() => (AMOUNT_PATTERN.test(input) && input !== '' && input !== '.' ? toUnits(input, decimals) : 0n), [input, decimals]);
  const principal = position?.principal ?? 0n;

  const problem = useMemo((): string | null => {
    if (!pool || amount === 0n) return null;
    if (pool.paused) return t.errPaused;
    if (amount > balance) return t.insufficientBalance;
    if (principal === 0n && amount < pool.minStake) return `${t.errBelowMinimum}: ${formatUnits(pool.minStake, decimals)} ${symbol}`;
    if (pool.maxTotalStaked !== 0n && pool.totalStaked + amount > pool.maxTotalStaked) return t.errCapExceeded;
    const reservation = lockReservation(principal + amount, pool.rateBps, pool.lockSeconds) - (position?.lockReservation ?? 0n);
    if (info!.rewardVaultBalance < pool.reservedRewards + reservation) return t.errRewardsUnderfunded;
    return null;
  }, [pool, info, amount, balance, principal, position, decimals, symbol, t]);

  const close = () => {
    setInput('');
    setConfirming(false);
    onClose();
  };

  const submit = async () => {
    try {
      await mutateAsync(amount);
      enqueueSnackbar(`${t.staked} ${formatUnits(amount, decimals)} ${symbol}`, { variant: 'success' });
      close();
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : String(err), { variant: 'error' });
      setConfirming(false);
    }
  };

  if (!open) return null;

  return (
    <DialogForm open={open} onClose={close} fullWidth>
      <DialogTitle>
        {t.stakeTitle} {symbol}
      </DialogTitle>
      <DialogContent>
        {isLoading || !pool ? (
          <LoadingIndicator delay={0} />
        ) : confirming ? (
          <>
            <Typography variant="h6" gutterBottom>
              {formatUnits(amount, decimals)} {symbol}
            </Typography>
            <DialogContentText>
              {t.lockedUntil} {formatDate(clock() + BigInt(pool.lockSeconds))}
            </DialogContentText>
            {principal > 0n && (
              <Alert severity="warning">
                {t.topUpRelocks.replace('%s', `${formatUnits(principal, decimals)} ${symbol}`)}
              </Alert>
            )}
          </>
        ) : (
          <>
            <DialogContentText>
              {formatBps(pool.rateBps)} {t.apr} · {Math.round(pool.lockSeconds / DAY)} {t.dayLock} · {t.minimum}{' '}
              {formatUnits(pool.minStake, decimals)} {symbol}
            </DialogContentText>
            {pool.paused && <Alert severity="info">{t.errPaused}</Alert>}
            {balance === 0n && <Alert severity="info">{t.noBalance.replace('%s', symbol)}</Alert>}
            <TextField
              label={strings.amount}
              fullWidth
              variant="outlined"
              margin="normal"
              value={input}
              onChange={(e) => AMOUNT_PATTERN.test(e.target.value) && setInput(e.target.value)}
              error={!!problem}
              helperText={problem ?? `${strings.max}: ${formatUnits(balance, decimals)} ${symbol}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={() => setInput(formatUnits(balance, decimals))}>{strings.max}</Button>
                    <Typography color="primary">{symbol}</Typography>
                  </InputAdornment>
                ),
              }}
            />
            {amount > 0n && !problem && (
              <DialogContentText>
                {t.projected}: {formatUnits(projectedRewards(amount, pool.rateBps, 30 * DAY), decimals, 6)} {symbol} / 30d ·{' '}
                {formatUnits(projectedRewards(amount, pool.rateBps, 365 * DAY), decimals, 6)} {symbol} / 1y
              </DialogContentText>
            )}
            {principal > 0n && (
              <Alert severity="warning">
                {t.topUpRelocks.replace('%s', `${formatUnits(principal, decimals)} ${symbol}`)}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={confirming ? () => setConfirming(false) : close} disabled={sending}>
          {confirming ? t.back : strings.cancel}
        </Button>
        {confirming ? (
          <Button color="primary" variant="contained" onClick={submit} disabled={sending}>
            {sending ? t.sending : t.confirmStake}
          </Button>
        ) : (
          <Button color="primary" onClick={() => setConfirming(true)} disabled={!pool || amount === 0n || !!problem}>
            {strings.stake}
          </Button>
        )}
      </DialogActions>
    </DialogForm>
  );
}
